import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useCreateInvoice,
  useJobInvoices,
  useUpdateInvoice,
} from '@/hooks/useInvoices';
import { useBreakdown } from '@/hooks/useBreakdown';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency } from '@/lib/utils';
import type { Invoice } from '@jobos/shared';
import { APIError } from '@/api/client';
import {
  DEFAULT_PAYMENT_TERMS,
  PaymentTermsField,
} from '@/components/billing/PaymentTermsField';
import { CashInvoiceField, useJobCashDefault } from '@/components/billing/CashInvoiceField';
import type { PaymentTerms } from '@jobos/shared';

// Dialog flow for generating a Final invoice.
//
// Why a dialog (not a direct one-click action):
//   - Amount is editable. The active doc total is just the suggested value;
//     the user can override (e.g. they want to bill less because partials
//     already covered most of the work, or more for a contingency).
//   - The info panel (Job Total / Paid / Unpaid / Suggested) gives the user
//     the context they need to pick a number for all three edge cases —
//     scope held steady, scope grew, scope shrunk.
//
// Void+reissue is handled inline: if the server returns 409 with an existing
// non-voided final, we show a confirmation; on confirm we void the old then
// retry the create. The unique-final partial index excludes voided rows so
// the retry succeeds.

interface FinalInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobCode: string;
  jobDescription: string | null;
  /**
   * Fired once the invoice exists. Optional, and only callers outside the job
   * page pass it: useCreateInvoice invalidates invoiceKeys.byJob(jobId), which
   * is everything the job page needs, but says nothing to the /invoices
   * workspace. The workspace invalidates its own counts and rows from here —
   * adding its query keys inside useCreateInvoice would couple a job-scoped
   * hook to a page that is usually not mounted.
   */
  onCreated?: (invoice: Invoice) => void;
}

interface ExistingFinalConflict {
  existingInvoiceId: string;
  existingInvoiceNumber: string;
  attemptedAmount: number;
  attemptedDescription: string;
}

export function FinalInvoiceDialog({
  open,
  onOpenChange,
  jobId,
  jobCode,
  jobDescription,
  onCreated,
}: FinalInvoiceDialogProps) {
  const { data: breakdown } = useBreakdown(jobId);
  const { data: invoices = [] } = useJobInvoices(jobId);
  const createInvoice = useCreateInvoice(jobId);
  const updateInvoice = useUpdateInvoice(jobId);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>(DEFAULT_PAYMENT_TERMS);
  // null = the user has not touched the switch, so the job's own invoice type
  // decides. Kept nullable rather than seeded into state because the job loads
  // asynchronously: a plain `useState(jobDefault)` would latch `false` on the
  // first render and never see the answer arrive.
  const [cashChoice, setCashChoice] = useState<boolean | null>(null);
  const jobIsCash = useJobCashDefault(jobId);
  const isCash = cashChoice ?? jobIsCash ?? false;
  const { toast } = useToast();

  const jobTotal = breakdown?.totalCost ?? 0;
  // Only PAID partials reduce the suggested final — they're money already
  // collected against the project total. Outstanding (unpaid) partials are
  // shown for context but NOT deducted: the final bills the full remaining
  // work, and those partials are expected to be settled or voided separately.
  const paidPartialTotal = invoices
    .filter((inv) => inv.status === 'paid' && inv.kind === 'partial')
    .reduce((sum, inv) => sum + inv.amount, 0);
  const unpaidPartialTotal = invoices
    .filter((inv) => inv.status === 'unpaid' && inv.kind === 'partial')
    .reduce((sum, inv) => sum + inv.amount, 0);
  // Suggested = job total minus what's already been PAID via partials. Floor at
  // 0 — if paid partials exceed the (possibly shrunk) job total the user has
  // over-collected and should refund / void rather than issue a negative final.
  const suggestedAmount = Math.max(0, jobTotal - paidPartialTotal);

  // Spec D5: the final credits prior partials at FACE VALUE and only when they
  // are FULLY paid. A partly-paid partial is therefore credited at $0 even
  // though real money has been collected against it. That crediting rule is
  // deliberately unchanged — so surface the anomaly here instead, where the
  // person deciding can see it.
  const partPaidPartials = invoices.filter(
    (inv) => inv.kind === 'partial' && inv.lifecycle === 'partially_paid',
  );

  const defaultDescription = `Final invoice for ${jobCode}${jobDescription ? ` — ${jobDescription}` : ''}`;
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState(defaultDescription);
  const [conflict, setConflict] = useState<ExistingFinalConflict | null>(null);

  // Re-prime defaults on every open so a stale draft from a previous open
  // doesn't leak across; also reseed when the suggested amount changes
  // (e.g. user paid an invoice while the dialog was closed).
  useEffect(() => {
    if (open) {
      setAmount(suggestedAmount > 0 ? suggestedAmount.toFixed(2) : '');
      setDescription(defaultDescription);
      setConflict(null);
      // Back to "follow the job" — a cash switch flipped off for the invoice
      // the user abandoned must not carry into the next one.
      setCashChoice(null);
    }
  }, [open, suggestedAmount, defaultDescription]);

  const parsedAmount = parseFloat(amount);
  const isValidAmount = !isNaN(parsedAmount) && parsedAmount > 0;
  // Remainder after paid partials only — matches the suggested amount so the
  // pre-filled default never trips the warning.
  const remainingUnbilled = jobTotal - paidPartialTotal;
  // Compare in integer cents so float noise in jobTotal (e.g. 4105.8199999)
  // can't make the cent-equal default trip the warning. Invoices are billed to
  // the cent, so cent-level comparison is the correct money comparison.
  const amountCents = Math.round(parsedAmount * 100);
  const remainingCents = Math.round(remainingUnbilled * 100);
  const exceedsBalance =
    isValidAmount && jobTotal > 0 && remainingCents > 0 && amountCents > remainingCents;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      // Conflict prompt has its own buttons; closing the dialog dismisses it.
      setConflict(null);
    }
    onOpenChange(nextOpen);
  };

  const submitCreate = async (overrideAmount: number, overrideDescription: string) => {
    const invoice = await createInvoice.mutateAsync({
      kind: 'final',
      amount: overrideAmount,
      description: overrideDescription,
      paymentTerms,
      isCash,
    });
    // Moves the row out of To Bill.
    onCreated?.(invoice);
    // No PDF is saved on create — the invoice row's own Download / View
    // actions are how the user gets the file when they actually want it.
    toast({ title: 'Final invoice created' });
    handleOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidAmount) return;

    try {
      await submitCreate(parsedAmount, description);
    } catch (err) {
      // 409 with an existing non-voided final → surface the void+reissue prompt
      // inline. Other errors surface through the mutation's toast.
      if (err instanceof APIError && err.status === 409) {
        const existingId = typeof err.data.existingInvoiceId === 'string'
          ? err.data.existingInvoiceId
          : null;
        const existingNumber = typeof err.data.existingInvoiceNumber === 'string'
          ? err.data.existingInvoiceNumber
          : null;
        if (existingId && existingNumber) {
          setConflict({
            existingInvoiceId: existingId,
            existingInvoiceNumber: existingNumber,
            attemptedAmount: parsedAmount,
            attemptedDescription: description,
          });
        }
      }
    }
  };

  const handleConfirmVoidAndReissue = async () => {
    if (!conflict) return;
    try {
      // Void first, then create. The partial unique index excludes voided
      // rows so the create succeeds; we await the void to ensure the DB
      // sees the new status before the next insert is attempted.
      await updateInvoice.mutateAsync({
        id: conflict.existingInvoiceId,
        status: 'voided',
        silent: true,
      });
      await submitCreate(conflict.attemptedAmount, conflict.attemptedDescription);
    } catch {
      // Mutation hooks surface toast errors; close the prompt either way so
      // the user isn't trapped.
      setConflict(null);
    }
  };

  const isBusy = createInvoice.isPending || updateInvoice.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{conflict ? 'Replace existing final invoice?' : 'Create Final Invoice'}</DialogTitle>
          {conflict && (
            <DialogDescription>
              Invoice <span className="font-mono">{conflict.existingInvoiceNumber}</span> is the current final invoice for this job. Voiding it and creating a new final invoice for {formatCurrency(conflict.attemptedAmount)} will leave the old one as a voided record (audit trail kept).
            </DialogDescription>
          )}
        </DialogHeader>

        {conflict ? (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConflict(null)}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmVoidAndReissue}
              disabled={isBusy}
              className="bg-blue-900 hover:bg-blue-800 text-white"
            >
              {isBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Void &amp; Create New
            </Button>
          </DialogFooter>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {jobTotal > 0 && (
              <div className="rounded-md bg-gray-50 px-3 py-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Active breakdown total</span>
                  <span className="font-medium">{formatCurrency(jobTotal)}</span>
                </div>
                {paidPartialTotal > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Partials — paid</span>
                    <span className="font-medium text-green-600">-{formatCurrency(paidPartialTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-gray-200 mt-1 pt-1">
                  <span className="text-gray-700 font-medium">Suggested final</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(suggestedAmount)}</span>
                </div>
                {unpaidPartialTotal > 0 && (
                  <div className="flex justify-between text-xs text-amber-600 mt-1">
                    <span>Outstanding partials (not deducted)</span>
                    <span className="font-medium">{formatCurrency(unpaidPartialTotal)}</span>
                  </div>
                )}
              </div>
            )}

            {/* Warn, never block: this is rare, and the user is better placed
                to judge it than the dialog is. */}
            {partPaidPartials.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm">
                <p className="font-medium text-amber-900">
                  {partPaidPartials.length === 1
                    ? 'A partly-paid partial will be credited at $0'
                    : `${partPaidPartials.length} partly-paid partials will be credited at $0`}
                </p>
                <ul className="mt-1 space-y-0.5 text-xs text-amber-800">
                  {partPaidPartials.map((inv) => (
                    <li key={inv.id} className="flex justify-between gap-3">
                      <span className="font-mono">{inv.invoiceNumber}</span>
                      <span>
                        {formatCurrency(inv.collected)} collected of {formatCurrency(inv.amount)}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 text-xs text-amber-800">
                  The final invoice credits partials at face value only once they are fully paid,
                  so the money already collected against these will not appear on it.
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="final-amount" className="text-sm font-medium">
                Amount
              </label>
              <input
                id="final-amount"
                type="number"
                step="0.01"
                min="0.01"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className={`flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${
                  exceedsBalance ? 'border-amber-300 focus-visible:ring-amber-500' : 'border-input'
                }`}
              />
              {exceedsBalance && (
                <p className="text-xs text-amber-600">
                  Amount exceeds the remainder after paid partials ({formatCurrency(remainingUnbilled)}). That&apos;s OK if you mean to — just double-check the figure.
                </p>
              )}
            </div>

            <PaymentTermsField
              id="final-payment-terms"
              value={paymentTerms}
              onChange={setPaymentTerms}
              disabled={isBusy}
            />

            <CashInvoiceField
              id="final-cash"
              checked={isCash}
              onChange={setCashChoice}
              disabled={isBusy}
            />

            <div className="space-y-1.5">
              <label htmlFor="final-description" className="text-sm font-medium">
                Description
              </label>
              <input
                id="final-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isBusy}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isBusy || !isValidAmount}
                className="bg-blue-900 hover:bg-blue-800 text-white"
              >
                {isBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Invoice
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
